import React, { useState, useEffect, useCallback } from 'react';
import styles from '../../pages/dashboard/DashboardUI.module.css';
import settingsStyles from '../../pages/dashboard/Settings.module.css';
import { getTimeOffTypes, createTimeOffType, updateTimeOffType, deleteTimeOffType } from '../../services/timeOffService';

export default function LeaveTypeManager({ onMessage }) {
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    default_allocation: ''
  });

  const loadLeaveTypes = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getTimeOffTypes();
      setLeaveTypes(data || []);
    } catch (error) {
      onMessage({ message: error.message || 'Failed to load leave types', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [onMessage]);

  useEffect(() => {
    loadLeaveTypes();
  }, [loadLeaveTypes]);

  const openCreateModal = () => {
    setEditingType(null);
    setFormData({ name: '', description: '', default_allocation: '' });
    setShowModal(true);
  };

  const openEditModal = (type) => {
    setEditingType(type);
    setFormData({
      name: type.name,
      description: type.description || '',
      default_allocation: type.default_allocation || ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingType(null);
    setFormData({ name: '', description: '', default_allocation: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      onMessage({ message: 'Leave type name is required', type: 'error' });
      return;
    }

    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        default_allocation: formData.default_allocation ? parseInt(formData.default_allocation) : null
      };

      if (editingType) {
        const typeId = editingType.time_off_type_id;
        await updateTimeOffType(typeId, payload);
        onMessage({ message: 'Leave type updated successfully', type: 'success' });
      } else {
        await createTimeOffType(payload);
        onMessage({ message: 'Leave type created successfully', type: 'success' });
      }
      closeModal();
      await loadLeaveTypes();
    } catch (error) {
      onMessage({ message: error.message || 'Failed to save leave type', type: 'error' });
    }
  };

  const handleDelete = async (type) => {
    const typeId = type.time_off_type_id;
    if (deleting === typeId) return;
    setDeleting(typeId);
    try {
      await deleteTimeOffType(typeId);
      onMessage({ message: `Leave type "${type.name}" deleted`, type: 'success' });
      await loadLeaveTypes();
    } catch (error) {
      onMessage({ message: error.message || 'Failed to delete leave type', type: 'error' });
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return <div className={settingsStyles.loading}>Loading leave types...</div>;
  }

  return (
    <div>
      <div className={settingsStyles.sectionHeader}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: '#1e293b' }}>
            Leave Types
          </h3>
          <p className={settingsStyles.cardDesc} style={{ margin: '4px 0 0 0' }}>
            Manage leave types and their default allocations for your company.
          </p>
        </div>
        <button className={styles.buttonPrimary} onClick={openCreateModal}>
          + New Leave Type
        </button>
      </div>

      {leaveTypes.length === 0 ? (
        <div className={settingsStyles.emptyState}>
          <p>No leave types yet. Create your first leave type to start tracking employee leave.</p>
        </div>
      ) : (
        <div className={settingsStyles.labelsList}>
          {leaveTypes.map((type) => {
            const typeId = type.time_off_type_id;
            return (
              <div key={typeId} className={settingsStyles.labelCard}>
                <div className={settingsStyles.labelInfo}>
                  <span
                    className={settingsStyles.labelBadge}
                    style={{ backgroundColor: '#185FA5', color: '#ffffff' }}
                  >
                    {type.name}
                  </span>
                  <span className={settingsStyles.labelDescription}>
                    {type.default_allocation ? `${type.default_allocation} days/year` : 'No default'}
                    {type.description ? ` · ${type.description}` : ''}
                  </span>
                </div>
                <div className={settingsStyles.roleActions}>
                  <button
                    className={styles.buttonSecondary}
                    onClick={() => openEditModal(type)}
                  >
                    Edit
                  </button>
                  <button
                    className={styles.buttonDanger}
                    onClick={() => handleDelete(type)}
                    disabled={deleting === typeId}
                  >
                    {deleting === typeId ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <div className={settingsStyles.modalOverlay} onClick={closeModal}>
          <div className={settingsStyles.modal} onClick={(e) => e.stopPropagation()} style={{ minHeight: 'auto' }}>
            <div className={settingsStyles.modalHeader}>
              <h3>{editingType ? 'Edit Leave Type' : 'Create Leave Type'}</h3>
              <button className={settingsStyles.closeButton} onClick={closeModal}>
                ×
              </button>
            </div>
            <div className={settingsStyles.modalContent}>
              <form onSubmit={handleSubmit}>
                <div className={settingsStyles.formGroup}>
                  <label>Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g. Annual Leave, Sick Leave"
                    autoFocus
                  />
                </div>

                <div className={settingsStyles.formGroup}>
                  <label>Default Allocation (days per year)</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={formData.default_allocation}
                    onChange={(e) => setFormData({ ...formData, default_allocation: e.target.value })}
                    placeholder="e.g. 25"
                  />
                </div>

                <div className={settingsStyles.formGroup}>
                  <label>Description (optional)</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="What is this leave type used for?"
                    rows={2}
                  />
                </div>

                <div className={settingsStyles.modalActions}>
                  <button className={styles.buttonSecondary} onClick={closeModal} type="button">
                    Cancel
                  </button>
                  <button className={styles.buttonPrimary} type="submit">
                    {editingType ? 'Save Changes' : 'Create Leave Type'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
